import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { activateUser, deactivateUser, resetUserPassword, resetUserTwoFactor } from '../api';
import AdminUserGroups from './AdminUserGroups';
import Button from './ui/Button';

function AdminUserRow({
  user,
  groups,
  assignableGroups = groups,
  isExpanded = false,
  isSelf = false,
  onToggle,
  onChanged,
}) {
  const { t } = useTranslation();

  const [newPassword, setNewPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  const [isConfirmingDeactivate, setIsConfirmingDeactivate] =
    useState(false);

  const [isConfirmingTwoFactorReset, setIsConfirmingTwoFactorReset] = useState(false);
  const [isResettingTwoFactor, setIsResettingTwoFactor] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorSuccess, setTwoFactorSuccess] = useState('');

  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [statusError, setStatusError] = useState('');

  // The server decides; an account the caller may only see is shown read-only
  // rather than with forms that would answer 404.
  const isManageable = user.manageable !== false;

  const displayName =
    [user.firstName, user.lastName]
      .filter(Boolean)
      .join(' ') || user.username;

  async function handleResetPassword(event) {
    event.preventDefault();

    if (newPassword.length < 8) {
      setResetError(t('adminPanel.errorPasswordLength'));
      return;
    }

    try {
      setIsResetting(true);
      setResetError('');
      setResetSuccess('');

      await resetUserPassword(user.userId, newPassword);

      setNewPassword('');
      setResetSuccess(t('adminPanel.resetPasswordSuccess'));
    } catch (submitError) {
      console.error(
        'Failed to reset password:',
        submitError,
      );

      setResetError(
        submitError.message ||
          t('adminPanel.errorGeneric'),
      );
    } finally {
      setIsResetting(false);
    }
  }

  // Lost authenticator: 2FA off, the person signs in with the password and
  // sets it up again (right away, where the platform requires it).
  async function handleResetTwoFactor() {
    try {
      setIsResettingTwoFactor(true);
      setTwoFactorError('');

      await resetUserTwoFactor(user.userId);

      setIsConfirmingTwoFactorReset(false);
      setTwoFactorSuccess(t('adminPanel.twoFactorResetDone'));
      onChanged();
    } catch (submitError) {
      console.error('Failed to reset two-factor authentication:', submitError);
      setTwoFactorError(submitError.message || t('adminPanel.errorGeneric'));
    } finally {
      setIsResettingTwoFactor(false);
    }
  }

  async function handleDeactivate() {
    try {
      setIsDeactivating(true);
      setStatusError('');

      await deactivateUser(user.userId);

      setIsConfirmingDeactivate(false);
      onChanged();
    } catch (submitError) {
      console.error(
        'Failed to deactivate user:',
        submitError,
      );

      setStatusError(
        submitError.message ||
          t('adminPanel.errorGeneric'),
      );
    } finally {
      setIsDeactivating(false);
    }
  }

  async function handleActivate() {
    try {
      setIsActivating(true);
      setStatusError('');

      await activateUser(user.userId);

      onChanged();
    } catch (submitError) {
      console.error('Failed to activate user:', submitError);

      setStatusError(submitError.message || t('adminPanel.errorGeneric'));
    } finally {
      setIsActivating(false);
    }
  }

  return (
    <div className="admin-user-row">
      <button
        type="button"
        className="admin-user-row-header"
        aria-expanded={isExpanded}
        onClick={onToggle}
      >
        <i
          className={`fa-solid fa-chevron-right admin-user-toggle-icon${
            isExpanded ? ' expanded' : ''
          }`}
        />

        <div className="admin-user-identity">
          <strong>{displayName}</strong>
          <span>@{user.username}</span>
        </div>

        {user.authProvider === 'oidc' && (
          <span
            className="admin-sso-badge"
            title={t('adminPanel.ssoAccount')}
          >
            SSO
          </span>
        )}

        <span
          className={`admin-user-status${
            user.isActive ? ' active' : ' inactive'
          }`}
        >
          {user.isActive
            ? t('adminPanel.statusActive')
            : t('adminPanel.statusInactive')}
        </span>
      </button>

      {isExpanded && !isManageable && (
        <div className="admin-user-row-body">
          <p className="admin-user-section-hint">
            {t('adminPanel.userOutsideScope')}
          </p>

          {user.authProvider !== 'oidc' && (
            <div className="admin-user-section">
              <h4>{t('adminPanel.twoFactorLabel')}</h4>

              <p className="admin-user-section-hint">
                {user.twoFactorEnabled
                  ? t('adminPanel.twoFactorOn')
                  : t('adminPanel.twoFactorOff')}
              </p>

              {twoFactorError && (
                <div className="login-message login-message-error" role="alert">
                  <i className="fa-solid fa-circle-exclamation" />
                  <span>{twoFactorError}</span>
                </div>
              )}

              {twoFactorSuccess && (
                <div className="login-message login-message-success" role="status">
                  <i className="fa-solid fa-circle-check" />
                  <span>{twoFactorSuccess}</span>
                </div>
              )}

              {user.twoFactorEnabled && !isConfirmingTwoFactorReset && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setTwoFactorSuccess('');
                    setIsConfirmingTwoFactorReset(true);
                  }}
                >
                  {t('adminPanel.twoFactorResetButton')}
                </Button>
              )}

              {user.twoFactorEnabled && isConfirmingTwoFactorReset && (
                <div className="admin-confirm-row">
                  <span>{t('adminPanel.twoFactorResetConfirm')}</span>

                  <Button
                    variant="secondary"
                    disabled={isResettingTwoFactor}
                    onClick={() => setIsConfirmingTwoFactorReset(false)}
                  >
                    {t('adminPanel.cancel')}
                  </Button>

                  <Button
                    variant="secondary"
                    className="admin-danger-button"
                    disabled={isResettingTwoFactor}
                    onClick={handleResetTwoFactor}
                  >
                    {isResettingTwoFactor
                      ? t('adminPanel.twoFactorResetting')
                      : t('adminPanel.twoFactorResetConfirmButton')}
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="admin-user-section">
            <h4>{t('adminPanel.membershipsLabel')}</h4>

            <AdminUserGroups user={user} groups={groups} readOnly />
          </div>
        </div>
      )}

      {isExpanded && isManageable && (
        <div className="admin-user-row-body">
          {user.authProvider === 'oidc' ? (
            <div className="admin-user-section">
              <h4>{t('adminPanel.resetPasswordLabel')}</h4>

              <p className="admin-user-section-hint">
                {t('adminPanel.ssoNoPassword')}
              </p>
            </div>
          ) : (
            <div className="admin-user-section">
              <h4>{t('adminPanel.resetPasswordLabel')}</h4>

              {resetError && (
                <div
                  className="login-message login-message-error"
                  role="alert"
                >
                  <i className="fa-solid fa-circle-exclamation" />
                  <span>{resetError}</span>
                </div>
              )}

              {resetSuccess && (
                <div
                  className="login-message login-message-success"
                  role="status"
                >
                  <i className="fa-solid fa-circle-check" />
                  <span>{resetSuccess}</span>
                </div>
              )}

              <form
                className="admin-inline-form"
                onSubmit={handleResetPassword}
                noValidate
              >
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('adminPanel.passwordLabel')}
                  value={newPassword}
                  disabled={isResetting || !user.isActive}
                  onChange={(event) => {
                    setNewPassword(event.target.value);
                    setResetError('');
                  }}
                />

                <Button
                  variant="primary"
                  type="submit"
                  disabled={isResetting || !user.isActive}
                >
                  {isResetting
                    ? t('adminPanel.resettingPassword')
                    : t('adminPanel.resetPasswordButton')}
                </Button>
              </form>
            </div>
          )}

          {user.authProvider !== 'oidc' && (
            <div className="admin-user-section">
              <h4>{t('adminPanel.twoFactorLabel')}</h4>

              <p className="admin-user-section-hint">
                {user.twoFactorEnabled
                  ? t('adminPanel.twoFactorOn')
                  : t('adminPanel.twoFactorOff')}
              </p>

              {twoFactorError && (
                <div className="login-message login-message-error" role="alert">
                  <i className="fa-solid fa-circle-exclamation" />
                  <span>{twoFactorError}</span>
                </div>
              )}

              {twoFactorSuccess && (
                <div className="login-message login-message-success" role="status">
                  <i className="fa-solid fa-circle-check" />
                  <span>{twoFactorSuccess}</span>
                </div>
              )}

              {user.twoFactorEnabled && !isConfirmingTwoFactorReset && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setTwoFactorSuccess('');
                    setIsConfirmingTwoFactorReset(true);
                  }}
                >
                  {t('adminPanel.twoFactorResetButton')}
                </Button>
              )}

              {user.twoFactorEnabled && isConfirmingTwoFactorReset && (
                <div className="admin-confirm-row">
                  <span>{t('adminPanel.twoFactorResetConfirm')}</span>

                  <Button
                    variant="secondary"
                    disabled={isResettingTwoFactor}
                    onClick={() => setIsConfirmingTwoFactorReset(false)}
                  >
                    {t('adminPanel.cancel')}
                  </Button>

                  <Button
                    variant="secondary"
                    className="admin-danger-button"
                    disabled={isResettingTwoFactor}
                    onClick={handleResetTwoFactor}
                  >
                    {isResettingTwoFactor
                      ? t('adminPanel.twoFactorResetting')
                      : t('adminPanel.twoFactorResetConfirmButton')}
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="admin-user-section">
            <h4>{t('adminPanel.membershipsLabel')}</h4>

            <p className="admin-user-section-hint">
              {t('adminPanel.membershipsHint')}
            </p>

            <AdminUserGroups
              user={user}
              groups={groups}
              assignableGroups={assignableGroups}
              onChanged={onChanged}
            />
          </div>

          <div className="admin-user-section">
            {statusError && (
              <div
                className="login-message login-message-error"
                role="alert"
              >
                <i className="fa-solid fa-circle-exclamation" />
                <span>{statusError}</span>
              </div>
            )}

            {/* The server refuses it too: deactivating oneself ends one's
                own session and can lock the last administrator out. */}
            {user.isActive && isSelf && (
              <>
                <Button
                  variant="secondary"
                  className="admin-danger-button"
                  disabled
                  title={t('adminPanel.cannotDeactivateSelf')}
                >
                  {t('adminPanel.deactivateButton')}
                </Button>

                <p className="admin-disabled-reason">
                  {t('adminPanel.cannotDeactivateSelf')}
                </p>
              </>
            )}

            {!user.isActive && (
              <Button
                variant="secondary"
                disabled={isActivating}
                onClick={handleActivate}
              >
                {isActivating
                  ? t('adminPanel.activating')
                  : t('adminPanel.activateButton')}
              </Button>
            )}

            {user.isActive && !isSelf && !isConfirmingDeactivate && (
              <Button
                variant="secondary"
                className="admin-danger-button"
                onClick={() => setIsConfirmingDeactivate(true)}
              >
                {t('adminPanel.deactivateButton')}
              </Button>
            )}

            {user.isActive && isConfirmingDeactivate && (
              <div className="admin-confirm-row">
                <span>{t('adminPanel.deactivateConfirm')}</span>

                <Button
                  variant="secondary"
                  disabled={isDeactivating}
                  onClick={() => setIsConfirmingDeactivate(false)}
                >
                  {t('adminPanel.cancel')}
                </Button>

                <Button
                  variant="secondary"
                  className="admin-danger-button"
                  disabled={isDeactivating}
                  onClick={handleDeactivate}
                >
                  {isDeactivating
                    ? t('adminPanel.deactivating')
                    : t('adminPanel.deactivateConfirmButton')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUserRow;
